import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Upload, FileText, ChevronRight, CheckCircle2, AlertTriangle, X, ArrowLeft } from "lucide-react";

const TARGETS = [
    { id: "contacts", label: "Contatti / Lead", desc: "Clienti acquirenti, venditori, lead del CRM" },
    { id: "properties", label: "Annunci / Immobili", desc: "Immobili in vendita o affitto" },
];

const SUPPORTED = ".csv,.xlsx,.xls,.xml";

export default function CrmImport() {
    const [step, setStep] = useState(1); // 1: type, 2: upload, 3: mapping+preview, 4: result
    const [target, setTarget] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({});
    const [fieldsDef, setFieldsDef] = useState({ contacts: [], properties: [] });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/crm/import/fields").then((r) => setFieldsDef(r.data)).catch(() => {});
    }, []);

    const targetFields = useMemo(() => fieldsDef[target] || [], [fieldsDef, target]);

    const doPreview = async () => {
        if (!file || !target) return;
        setLoading(true); setError("");
        try {
            const fd = new FormData();
            fd.append("target", target);
            fd.append("file", file);
            const r = await api.post("/crm/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
            setMapping(r.data.auto_mapping || {});
            setStep(3);
        } catch (e) {
            setError(e.response?.data?.detail || "Errore lettura file");
        } finally {
            setLoading(false);
        }
    };

    const doExecute = async () => {
        setLoading(true); setError("");
        try {
            const fd = new FormData();
            fd.append("target", target);
            fd.append("file", file);
            fd.append("mapping_json", JSON.stringify(mapping));
            const r = await api.post("/crm/import/execute", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(r.data);
            setStep(4);
        } catch (e) {
            setError(e.response?.data?.detail || "Errore importazione");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setStep(1); setTarget(""); setFile(null); setPreview(null); setMapping({}); setResult(null); setError("");
    };

    const requiredMissing = useMemo(() => {
        const mapped = new Set(Object.values(mapping).filter(Boolean));
        return targetFields.filter((f) => f.required && !mapped.has(f.key));
    }, [targetFields, mapping]);

    return (
        <div data-testid="crm-import-page">
            <div className="mb-8">
                <span className="label-eyebrow text-gold">VD Manager</span>
                <h2 className="font-serif text-3xl text-ink-50 mt-2">Importa Dati</h2>
                <p className="text-ink-400 text-sm mt-2">Migra contatti, lead e annunci da un altro gestionale in pochi passi. Formati supportati: CSV, Excel (XLS/XLSX), XML.</p>
            </div>

            {/* Stepper */}
            <div className="flex items-center gap-3 mb-8 text-[11px] uppercase tracking-wider">
                {["1. Tipo dati", "2. File", "3. Anteprima", "4. Riepilogo"].map((s, i) => {
                    const idx = i + 1;
                    const active = step === idx;
                    const done = step > idx;
                    return (
                        <div key={s} className="flex items-center gap-3">
                            <span className={`px-3 py-1.5 border ${active ? "border-gold text-gold bg-gold/10" : done ? "border-gold/40 text-gold/70" : "border-white/15 text-ink-400"}`}>{s}</span>
                            {i < 3 && <ChevronRight size={12} className="text-ink-400" />}
                        </div>
                    );
                })}
            </div>

            {error && (
                <div className="mb-6 p-4 border border-red-500/40 bg-red-900/20 text-red-200 text-sm flex items-start gap-2">
                    <AlertTriangle size={14} className="mt-0.5" /> {error}
                </div>
            )}

            {/* STEP 1: seleziona tipo */}
            {step === 1 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {TARGETS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`imp-target-${t.id}`}
                            onClick={() => { setTarget(t.id); setStep(2); }}
                            className="text-left p-6 border border-white/15 hover:border-gold/60 transition-colors"
                        >
                            <div className="label-eyebrow text-gold mb-2">Importa</div>
                            <div className="font-serif text-xl text-ink-50">{t.label}</div>
                            <div className="text-xs text-ink-400 mt-2">{t.desc}</div>
                        </button>
                    ))}
                </div>
            )}

            {/* STEP 2: upload file */}
            {step === 2 && (
                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <button onClick={() => { setStep(1); setFile(null); }} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1"><ArrowLeft size={12}/> Cambia tipo dati</button>
                        <span className="text-xs text-ink-400">·</span>
                        <span className="text-xs text-gold">{TARGETS.find((t) => t.id === target)?.label}</span>
                    </div>
                    <div className="border border-dashed border-white/20 p-10 text-center">
                        <FileText size={28} className="text-gold mx-auto mb-4" />
                        <div className="text-sm text-ink-100 mb-4">Carica il file esportato dal tuo gestionale</div>
                        <input
                            data-testid="imp-file-input"
                            type="file"
                            accept={SUPPORTED}
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="block mx-auto text-xs text-ink-300 file:mr-3 file:py-2 file:px-4 file:border file:border-gold/40 file:bg-transparent file:text-gold file:text-xs hover:file:bg-gold/10 file:cursor-pointer"
                        />
                        <div className="text-[11px] text-ink-500 mt-3">Formati: CSV, XLSX, XLS, XML · max 10 MB</div>
                        {file && <div className="mt-4 text-xs text-gold">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button
                            data-testid="imp-preview-btn"
                            onClick={doPreview}
                            disabled={!file || loading}
                            className="cta-gold px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            {loading ? "Analisi..." : <><Upload size={14}/> Leggi anteprima</>}
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 3: preview + mapping */}
            {step === 3 && preview && (
                <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <button onClick={() => setStep(2)} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1"><ArrowLeft size={12}/> Cambia file</button>
                    </div>

                    <div className="p-4 border border-gold/30 bg-gold/5 mb-6 flex items-center justify-between">
                        <div>
                            <div className="text-xs text-ink-400">File: <span className="text-ink-100">{preview.filename}</span></div>
                            <div className="text-xs text-ink-400 mt-1">Righe rilevate: <span className="text-gold">{preview.total_rows}</span> · colonne: <span className="text-gold">{preview.columns.length}</span></div>
                        </div>
                        {requiredMissing.length > 0 && (
                            <div className="text-xs text-amber-300 flex items-center gap-2">
                                <AlertTriangle size={12}/> Mappa i campi: {requiredMissing.map((f) => f.label).join(", ")}
                            </div>
                        )}
                    </div>

                    <div className="mb-4 text-xs uppercase tracking-wider text-ink-400">Associa le colonne del file ai campi del gestionale</div>
                    <div className="border border-white/10 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-white/10 bg-white/5">
                                    <th className="text-left p-3 font-normal text-ink-400">Colonna file</th>
                                    <th className="text-left p-3 font-normal text-ink-400">Anteprima (primi valori)</th>
                                    <th className="text-left p-3 font-normal text-ink-400">→ Campo gestionale</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.columns.map((col) => (
                                    <tr key={col} className="border-b border-white/5">
                                        <td className="p-3 text-ink-100 font-mono text-[11px]">{col}</td>
                                        <td className="p-3 text-ink-400">
                                            {preview.sample_rows.slice(0, 3).map((r, i) => (
                                                <span key={i} className="inline-block mr-2 truncate max-w-[160px] align-top">{r[col] || <em className="text-ink-600">—</em>}</span>
                                            ))}
                                        </td>
                                        <td className="p-3">
                                            <select
                                                data-testid={`imp-map-${col}`}
                                                value={mapping[col] || ""}
                                                onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                                                className="bg-transparent border border-white/15 px-2 py-1.5 text-xs text-ink-100 focus:border-gold outline-none min-w-[220px]"
                                            >
                                                <option value="" className="bg-ink-950">— Ignora questa colonna —</option>
                                                {targetFields.map((f) => (
                                                    <option key={f.key} value={f.key} className="bg-ink-950">{f.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 flex justify-between items-center">
                        <div className="text-xs text-ink-400">Verranno importate {preview.total_rows} righe nel gestionale</div>
                        <button
                            data-testid="imp-execute-btn"
                            onClick={doExecute}
                            disabled={loading || requiredMissing.length > 0}
                            className="cta-gold px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Importazione..." : <>Conferma e importa <ChevronRight size={14}/></>}
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 4: result */}
            {step === 4 && result && (
                <div className="mb-8">
                    <div className="p-8 border border-gold/40 bg-gold/5 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <CheckCircle2 size={20} className="text-gold"/>
                            <div className="font-serif text-2xl text-ink-50">Importazione completata</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400">Totale righe</div>
                                <div className="font-serif text-3xl text-ink-50 mt-1">{result.total_rows}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400">Importati con successo</div>
                                <div className="font-serif text-3xl text-gold mt-1">{result.imported}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400">Saltati</div>
                                <div className="font-serif text-3xl text-amber-300 mt-1">{result.skipped}</div>
                            </div>
                        </div>
                    </div>

                    {result.errors && result.errors.length > 0 && (
                        <div className="border border-white/10 p-4 mb-6">
                            <div className="text-xs uppercase tracking-wider text-ink-400 mb-3 flex items-center gap-2">
                                <AlertTriangle size={12}/> Dettaglio righe saltate ({result.errors.length})
                            </div>
                            <div className="max-h-64 overflow-y-auto text-xs space-y-1">
                                {result.errors.map((e, i) => (
                                    <div key={i} className="flex gap-3 py-1 border-b border-white/5">
                                        <span className="text-ink-500 shrink-0">Riga {e.row}</span>
                                        <span className="text-ink-300">{e.reason}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <button data-testid="imp-reset-btn" onClick={reset} className="px-5 py-2.5 text-sm border border-white/15 text-ink-100 hover:border-gold hover:text-gold flex items-center gap-2">
                        <X size={12}/> Importa un altro file
                    </button>
                </div>
            )}
        </div>
    );
}
